import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider, useForm } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import {
  Checkbox,
  FormDialog,
  FormLayout,
  FormSection,
  FormRow as Row,
  TextInput,
} from '~/components';
import { TermsContent } from '~/pages/Terms';
import { logger } from '~/utils/logger';
import { WELCOME_FORM_ID } from '../constants';
import { providerRegistrationView } from '../registration-view.machine';
import { WelcomeSchema, WelcomeValues } from '../types';

const TAGS = CT.registration.welcome;

const log = logger.child('WelcomeForm');

interface WelcomeFormProps {
  actorRef: ActorRefFrom<typeof providerRegistrationView>;
  defaultValues?: WelcomeValues;
  loading?: boolean;
}

const ACCEPT_TOS_LABEL =
  'I confirm that I have read and accept the terms and conditions and privacy policy';

export const WelcomeForm = ({ actorRef, defaultValues }: WelcomeFormProps) => {
  const { tosModalOpen } = useSelector(actorRef, (state) => state.context.welcomeForm);

  const methods = useForm<WelcomeValues>({
    defaultValues: defaultValues || {
      email: '',
      phone: '',
      mfa: false,
      tosAccepted: false,
    },
    resolver: zodResolver(WelcomeSchema),
    mode: 'onBlur',
  });

  const onSubmitSuccess = (data: WelcomeValues) => {
    log.debug('Welcome Form Submit: ', data);
    actorRef.send({
      type: 'ON_SUBMIT_WELCOME',
      data,
    });
  };

  const onSubmitError = (err: unknown) => {
    log.warn('Welcome Form Submit Error: ', err);
  };

  const handleOpenTos = () => actorRef.send({ type: 'ON_OPEN_MODAL' });
  const handleCloseTos = () => actorRef.send({ type: 'ON_CLOSE_MODAL' });

  return (
    <FormProvider {...methods}>
      <form id={WELCOME_FORM_ID} onSubmit={methods.handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          {/* [MCV-1577] FormSection IS the Fieldset this step used to hand-roll
              (see components/forms/FormSection.tsx), so the step now stacks its
              fields at the template's 20px instead of a private 16px. */}
          <FormSection componentsPerLine={1}>
            <Row>
              <TextInput name={'email'} cyTag={''} label={'Email address'} required />
            </Row>

            {/* <Row> */}
            {/*   <div data-cy={TAGS.mfaError} className="grid grid-cols-[70%_30%] gap-4"> */}
            {/*     {methods.watch('mfa') ? ( */}
            {/*       <div className="w-full h-[75px]"> */}
            {/*         <MaskedInput */}
            {/*           mask={'+### (###) ###-####'} */}
            {/*           replacement={{ '#': /\d/ }} */}
            {/*           name={'phone'} */}
            {/*           cyTag={TAGS.phone} */}
            {/*           label={'Phone number'} */}
            {/*         /> */}
            {/*       </div> */}
            {/*     ) : ( */}
            {/*       <div className="w-full h-[75px]" /> */}
            {/*     )} */}
            {/*     <div className="flex justify-end items-center mt-4 mr-10"> */}
            {/*       <Toggle cyTag={TAGS.mfa} name={'mfa'} description={'MFA'} /> */}
            {/*     </div> */}
            {/*   </div> */}
            {/* </Row> */}

            <Row>
              <div
                data-cy={TAGS.tosModalOpen}
                onClick={handleOpenTos}
                className="h-50 w-full overflow-y-scroll cursor-pointer rounded-md border border-grey-900 bg-white px-4 py-3 text-left"
              >
                <TermsContent />
              </div>
              <Checkbox
                name={'tosAccepted'}
                cyTag={TAGS.tosToggle}
                errorCyTag={TAGS.tosError}
                style="border border-grey-800 border-1"
                label={ACCEPT_TOS_LABEL}
                rowReverse={true}
                required
              />
            </Row>
          </FormSection>
        </FormLayout>

        <FormDialog
          creating={false}
          editing={tosModalOpen}
          onClose={handleCloseTos}
          title={'Terms of Service'}
        >
          <div className="overflow-y-auto max-h-[70vh] px-4 py-3">
            <TermsContent />
          </div>
          <div className="px-4 pt-3 pb-2">
            <Checkbox
              name={'tosAccepted'}
              controlId="tosAccepted-dialog"
              cyTag={TAGS.tosToggle}
              style="border border-grey-800 border-1"
              label={ACCEPT_TOS_LABEL}
              rowReverse={true}
              required
            />
          </div>
        </FormDialog>
      </form>
    </FormProvider>
  );
};
